import { useState, useEffect, useCallback } from "react";

import LimitedLiabilityBusinessInformation from "./Steps/LimitedLiabilityBusinessInformation";
import LimitedLiabilityDirectors from "./Steps/LimitedLiabilityDirectors";
import LimitedLiabilityBusinessDocuments from "./Steps/LimitedLiabilityBusinessDocuments";
import LimitedLiabilityCompleteOnboarding from "./Steps/LimitedLiabilityCompleteOnboarding";
import HeaderTitle from "../../Components/HeaderTitle";
import styles from "./LimitedLiability.module.scss";
import useDocumentsByCustomerSubType from "../../hooks/useDocumentsByCustomerSubType";
import { BusinessTypes } from "../../../Interfaces";

import { ICorporateCustomer } from "../../hooks/useCorporateCustomers";

export enum AllLimitedLiabilitySteps {
  BUSINESS_INFORMATION = 0,
  DIRECTORS = 1,
  BUSINESS_DOCUMENTS = 2,
  COMPLETE_ONBOARDING = 3,
}

interface ILimitedLiability {
  onComplete: () => void;
  onBack?: () => void;
  onBackHandlerReady?: (handler: (() => void) | null) => void;
  formData?: any;
  onStepChange?: (step: number) => void;
  navigateToStep?: number; // Allow parent to control which step to navigate to
  resumeCustomerData?: ICorporateCustomer;
}

const LimitedLiability: React.FC<ILimitedLiability> = ({
  onComplete,
  onBack,
  onBackHandlerReady,
  formData = {},
  onStepChange,
  navigateToStep,
  resumeCustomerData,
}) => {
  const [currentStep, setCurrentStep] = useState(
    AllLimitedLiabilitySteps.BUSINESS_INFORMATION
  );

  const { data: limitedLiabilityDocuments, isLoading: isLoadingDocuments } =
    useDocumentsByCustomerSubType(BusinessTypes.LIMITED_LIABILITY.toString());

  // Sync with parent stepper navigation (subtract 1 because parent stepper has "Get Started" as step 0)
  useEffect(() => {
    if (navigateToStep !== undefined && navigateToStep >= 1) {
      const internalStep = navigateToStep - 1;
      if (
        internalStep !== currentStep &&
        internalStep >= 0 &&
        internalStep <= AllLimitedLiabilitySteps.COMPLETE_ONBOARDING
      ) {
        setCurrentStep(internalStep);
      }
    }
  }, [navigateToStep]);

  const handleProceed = (_step: number) => {
    setCurrentStep(_step);
    // Notify parent about step change (add 1 because "Get Started" is step 0 in parent stepper)
    if (onStepChange) {
      onStepChange(_step + 1);
    }
  };

  // Notify parent when current step changes
  useEffect(() => {
    if (onStepChange) {
      onStepChange(currentStep + 1);
    }
  }, [currentStep, onStepChange]);

  const handleBackStep = useCallback(() => {
    // Navigate backwards through steps
    switch (currentStep) {
      case AllLimitedLiabilitySteps.COMPLETE_ONBOARDING:
        setCurrentStep(AllLimitedLiabilitySteps.BUSINESS_DOCUMENTS);
        if (onStepChange) {
          onStepChange(AllLimitedLiabilitySteps.BUSINESS_DOCUMENTS + 1);
        }
        break;
      case AllLimitedLiabilitySteps.BUSINESS_DOCUMENTS:
        setCurrentStep(AllLimitedLiabilitySteps.DIRECTORS);
        if (onStepChange) {
          onStepChange(AllLimitedLiabilitySteps.DIRECTORS + 1);
        }
        break;
      case AllLimitedLiabilitySteps.DIRECTORS:
        setCurrentStep(AllLimitedLiabilitySteps.BUSINESS_INFORMATION);
        if (onStepChange) {
          onStepChange(AllLimitedLiabilitySteps.BUSINESS_INFORMATION + 1);
        }
        break;
      case AllLimitedLiabilitySteps.BUSINESS_INFORMATION:
        // We're at the first step, go back to parent step
        if (onBack) {
          onBack();
        }
        break;
      default:
        break;
    }
  }, [currentStep, onStepChange, onBack]);

  // Expose the back handler to parent via callback
  useEffect(() => {
    if (onBackHandlerReady) {
      onBackHandlerReady(handleBackStep);
    }
    // Cleanup: unregister handler when component unmounts
    return () => {
      if (onBackHandlerReady) {
        onBackHandlerReady(null);
      }
    };
  }, [onBackHandlerReady, handleBackStep]);

  const getStepTitle = () => {
    switch (currentStep) {
      case AllLimitedLiabilitySteps.BUSINESS_INFORMATION:
        return "Business Information";
      case AllLimitedLiabilitySteps.DIRECTORS:
        return "Directors";
      case AllLimitedLiabilitySteps.BUSINESS_DOCUMENTS:
        return "Business Documents";
      case AllLimitedLiabilitySteps.COMPLETE_ONBOARDING:
        return "Complete Onboarding";
      default:
        return "";
    }
  };

  const getStepSubtitle = () => {
    if (currentStep === AllLimitedLiabilitySteps.COMPLETE_ONBOARDING) {
      return "Review your onboarding progress and complete any outstanding steps.";
    }
    return "Kindly enter your information below";
  };

  return (
    <div className={styles.limitedLiability}>
      <HeaderTitle>{getStepTitle()}</HeaderTitle>
      <p className={styles.subtitle}>{getStepSubtitle()}</p>

      <div className={styles.content}>
        {(() => {
          switch (currentStep) {
            case AllLimitedLiabilitySteps.BUSINESS_INFORMATION:
              return (
                <LimitedLiabilityBusinessInformation
                  handleProceed={handleProceed}
                  resumeCustomerData={resumeCustomerData!}
                  formData={formData}
                />
              );

            case AllLimitedLiabilitySteps.DIRECTORS:
              return (
                <LimitedLiabilityDirectors
                  handleProceed={handleProceed}
                  formData={formData}
                  resumeCustomerData={resumeCustomerData}
                />
              );

            case AllLimitedLiabilitySteps.BUSINESS_DOCUMENTS:
              return (
                <LimitedLiabilityBusinessDocuments
                  handleProceed={handleProceed}
                  formData={formData}
                  documentTypes={limitedLiabilityDocuments?.data!}
                  resumeCustomerData={resumeCustomerData}
                />
              );

            case AllLimitedLiabilitySteps.COMPLETE_ONBOARDING:
              return (
                <LimitedLiabilityCompleteOnboarding
                  setCurrentStep={setCurrentStep}
                  onComplete={onComplete}
                  formData={formData}
                  resumeCustomerData={resumeCustomerData}
                />
              );

            default:
              return null;
          }
        })()}
      </div>
    </div>
  );
};

export default LimitedLiability;
